/*******************************************************************************
 * Copyright     : Copyright (c)  2024 Honeywell Inc. All rights reserved.
 *                 This work contains trade secrets and confidential materials
 *                 of Honeywell Inc.No part may be reproduced or transmitted in any form by any
 *                 means or for anypurpose without the express written permission of Honeywell. *
 *******************************************************************************/
import React, { useState, useEffect } from "react";

import { useTranslation } from "react-i18next";
import { BrowserRouter } from "react-router-dom";
import { OidcProvider } from "@axa-fr/react-oidc";
import { useDispatch, useSelector } from "react-redux";

import StandAloneRoutes from "./StandAloneRoutes";

import { OidcConfig } from "../../oidcConfig";

import { RootState } from "../../store";

import { setRBMAccessToken } from "../../actions";

import { IBuildOrganisation } from "../../models/devicelist";
import { OrgListProps, SiteMetaDataProps } from "../../models";

import { useGetOrgListsQuery, useGetUserSettingsDetailsQuery } from "../../services/rbm-api";

import { getThemeName } from "../../utils/utility";
import { IAM_PROJECT_ID_SESSION_TOKEN_STORAGE_NAME, RBM_ACCESS_TOKEN_STORAGE_NAME } from "../../utils/constants";

import './standalone.scss';

const StandaloneApplication = (props: { type: string }) => {
	console.log("Standalone Application: ", props);
	const dispatch = useDispatch();
	const { i18n } = useTranslation();

	const [loadType, setLoadType] = useState<string>("");

	const { selectedOrg, rbmAccessToken } = useSelector((state: RootState) => state.global);

	const { data: orgDataLists, isError, isLoading, isSuccess } = useGetOrgListsQuery("", {
		skip: !!selectedOrg || !rbmAccessToken
	});

	const { data: userSettingsDetails } = useGetUserSettingsDetailsQuery("");

	useEffect(() => {
		if (userSettingsDetails) {
			let tempLocale = "";

			userSettingsDetails?.settings?.forEach((setting: { value?: { prefs?: SiteMetaDataProps[] } }) => {
				if (setting?.value?.prefs?.length > 0) {
					tempLocale = setting.value?.prefs?.find((val: SiteMetaDataProps) => val.name === "language")?.value || "";
				}
			});

			if (tempLocale) {
				i18n.changeLanguage(tempLocale.replace("-", ""));
			}
		}
	}, [userSettingsDetails]);

	useEffect(() => {
		if (orgDataLists?.resources?.length > 0) {
			const siOrgLists: OrgListProps[] = [];
			const nonSIOrgLists: OrgListProps[] = [];

			orgDataLists?.resources?.forEach((item: IBuildOrganisation) => {
				if (item.zuoraAccountNo || item.orgType === "Contractor") {
					siOrgLists.push({
						text: item.name,
						value: item.id
					});
				} else {
					nonSIOrgLists.push({
						text: item.name,
						value: item.id
					});
				}
			});

			const sortSIOrgLists = siOrgLists.sort((a, b) => a.text.localeCompare(b.text));

			dispatch({
				type: 'SET_SI_ORG_LISTS', payload: {
					siOrgLists: sortSIOrgLists
				}
			});
			if (siOrgLists.length === 0) {
				setLoadType("noSIOrg");
			} else {
				setLoadType("");
			}

			const tempnonSIOrgLists = nonSIOrgLists.sort((a, b) => a.text.localeCompare(b.text));

			dispatch({
				type: 'SET_ORG_LIST', payload: {
					orgLists: tempnonSIOrgLists,
					selectedOrg: sortSIOrgLists[0]?.value || ""
				}
			});

			sessionStorage.setItem(IAM_PROJECT_ID_SESSION_TOKEN_STORAGE_NAME, siOrgLists[0]?.value || "");
		} else if (isError) {
			setLoadType("error");
		} else if (isLoading) {
			setLoadType("loading");
		} else if (isSuccess) {
			setLoadType("noSIOrg");
		} else {
			setLoadType("");
		}

	}, [isError, isSuccess, isLoading, orgDataLists]);

	useEffect(() => {
		const tokenVal = JSON.parse(sessionStorage.getItem("oidc.default") || '{}')?.tokens?.accessToken || '';

		sessionStorage.setItem(RBM_ACCESS_TOKEN_STORAGE_NAME, tokenVal);

		dispatch(setRBMAccessToken({ rbmAccessToken: tokenVal }));
	}, [sessionStorage.getItem("oidc.default")]);

	return (
		<BrowserRouter>
			<section className={`theme-${getThemeName("1")} iotdevices-container`}>
				<OidcProvider configuration={OidcConfig}>
					<StandAloneRoutes loadType={loadType} />
				</OidcProvider>
			</section>
		</BrowserRouter>
	);
};

export default StandaloneApplication;   